/**
 * Create Raw Product Modal Component
 *
 * Modal for creating a new raw product.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { X } from 'lucide-react';
import { Button } from '@/components/ui';
import { RawProductForm } from './RawProductForm';
import { useCreateRawProduct } from '../hooks/useRawProducts';
import { createRawProductSchema } from '@bstock/shared';
import type { CreateRawProductDto } from '@bstock/shared';
import { MarketType } from '@bstock/shared';

interface CreateRawProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (productId: string) => void;
}

export function CreateRawProductModal({ isOpen, onClose, onSuccess }: CreateRawProductModalProps) {
  const form = useForm<CreateRawProductDto>({
    resolver: zodResolver(createRawProductSchema),
    defaultValues: {
      name: '',
      sku: '',
      categoryId: '',
      marketType: MarketType.FOOD,
      recipeUnitId: '',
      purchaseVatRate: 5.5,
      saleVatRate: 5.5,
      allergens: [],
      description: null,
      barcode: null,
      minStock: null,
      maxStock: null,
    },
  });

  const createMutation = useCreateRawProduct();

  const handleSubmit = async (data: CreateRawProductDto) => {
    try {
      const product = await createMutation.mutateAsync(data);
      form.reset();
      onClose();
      onSuccess?.(product.id);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-dark-900/40 backdrop-blur-[2px] transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-60 w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-xl shadow-xl animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-dark-500">
            Creer un produit brut
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <RawProductForm form={form} mode="create" />

          {/* Global form error display (for refine errors not caught by fields) */}
          {form.formState.errors.root && (
            <div className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
              {form.formState.errors.root.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
          <Button variant="ghost" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={createMutation.isPending}
          >
            Creer le produit
          </Button>
        </div>
      </div>
    </div>
  );
}
